import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowRight, 
  faBolt, 
  faShieldAlt, 
  faLeaf, 
  faBuilding,
  faNetworkWired,
  faElevator
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import '../../styles/HomePage.css';

// Import project images - Updated with correct paths
import lessingImg from '../../assets/images/referenzen/01 Lessing/lessing.png';
import ckgImg from '../../assets/images/referenzen/02 CKG/ckg.png';
import schmittImg from '../../assets/images/referenzen/06 Schmitt+Sonn/schmittundsohn.png';
import lessingVideo from '../../assets/videos/lessing.mp4';
import tanzhausVideo from '../../assets/videos/tanzhaus.mp4';
import buergerhausVideo from '../../assets/videos/buergerhaus.mp4';

const HomePage: React.FC = () => {
  useEffect(() => {
    // Beim Wechsel auf Home Seite an den Anfang scrollen
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  const [activeSlide, setActiveSlide] = useState(0);
  
  // Hero slider media (images and videos)
  const heroSlides = [
    {
      video: lessingVideo,
      title: 'IB Neubaur – Technik mit Präzision',
      text: 'Wir planen Sicherheit, Komfort und Energieeffizienz mit System und hochqualifizierten Ingenieuren.'
    },
    {
      video: buergerhausVideo,
      title: 'Digitale Planung',
      text: 'Von der Idee zum detailgenauen 3D-Modell – präzise, koordiniert, wirtschaftlich.'
    },
    {
      image: lessingImg,
      title: 'IB Neubaur – Technik mit Präzision',
      text: 'Wir planen Sicherheit, Komfort und Energieeffizienz mit System und hochqualifizierten Ingenieuren.'
    },
    {
      image: ckgImg,
      title: 'Innovative Lösungen für komplexe Projekte',
      text: 'Nutzen Sie die Expertise von IB Neubaur. Wir verstärken Ihr Team und realisieren Ihre Visionen.'
    },
    {
      video: tanzhausVideo,
      title: 'Moderne Architektur trifft Technik',
      text: 'Innovative Gebäudetechnik für zeitgemäße Architektur – von der Planung bis zur Umsetzung.'
    }
  ];
  
  // Services data
  const services = [
    {
      title: 'Elektrotechnik',
      description: 'Planung und Beratung für alle Bereiche der Elektrotechnik mit modernsten Standards.',
      link: '/elektrotechnik',
      icon: faBolt
    },
    {
      title: 'Energietechnik',
      description: 'Energieeffiziente Lösungen für nachhaltige und wirtschaftliche Gebäudetechnik.',
      link: '/energietechnik',
      icon: faLeaf
    },
    {
      title: 'Fördertechnik',
      description: 'Planung von Aufzugsanlagen und Fördertechnik für optimale Mobilität.',
      link: '/foerdertechnik',
      icon: faElevator
    },
    {
      title: 'IuK',
      description: 'Moderne Informations- und Kommunikationstechnik für vernetzte Gebäude.',
      link: '/iuk',
      icon: faNetworkWired
    },
    {
      title: 'Sicherheitstechnik',
      description: 'Umfassende Sicherheitslösungen zum Schutz von Menschen und Werten.',
      link: '/sicherheitstechnik',
      icon: faShieldAlt
    }
  ];
  
  // Featured projects
  const featuredProjects = [
    {
      title: 'Lessing Grundschule',
      description: 'Neubau der Gotthold-Ephraim-Lessing Grundschule mit modernster Elektro- und Medientechnik.',
      image: lessingImg,
      link: '/referenzen'
    },
    {
      title: 'Christoph-Kolumbus-Grundschule',
      description: 'Schulneubau mit umfassender Elektro- und Netzwerktechnik für digitalen Unterricht.',
      image: ckgImg,
      link: '/referenzen'
    },
    {
      title: 'Schmitt + Sohn Verwaltungsgebäude',
      description: 'Neubau eines modernen Verwaltungsgebäudes mit intelligenter Gebäudetechnik.',
      image: schmittImg,
      link: '/referenzen'
    }
  ];
  
  // Auto-rotate hero slider
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % heroSlides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [heroSlides.length]);
  
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-slider">
          {heroSlides.map((slide, index) => (
            <div 
              key={index} 
              className={`hero-slide ${index === activeSlide ? 'active' : ''}`}
              style={slide.image ? { backgroundImage: `url(${slide.image})` } : undefined}
            >
              {slide.video && (
                <video
                  className="hero-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src={slide.video} />
                </video>
              )}
              <div className="hero-content">
                <div className="hero-content-box">
                  <h1 className="hero-title">{slide.title}</h1>
                  <p className="hero-text">{slide.text}</p>
                    {slide.video && (
                      <p className="hero-subtext">
                        BIM-gestützte 3D-Modellierung mit kollisionsfreier Koordination – der digitale Zwilling für Planung, Ausführung und Betrieb.
                      </p>
                    )}
                  <div className="hero-buttons">
                    <Link to="/ueber-uns" className="btn btn-primary">
                      Mehr über uns
                    </Link>
                    <Link to="/kontakt" className="btn btn-outline">
                      Kontakt aufnehmen
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      
      {/* Introduction Section */}
      <section className="section">
        <div className="container">
          <div className="section-title">
            <h2>IB Neubaur GmbH - Fachplanung Elektrotechnik</h2>
          </div>
          <div className="row">
            <div className="col">
              <p className="text-center mb-4">
                Unsere Aufgabe heißt Fachplanung für Elektrotechnik und bedeutet: 
                Wir planen Sicherheit, Komfort und Energieeffizienz mit System und hochqualifizierten
                Ingenieuren, Technikern und Zeichnern.
              </p>
              <p className="text-center mb-4">
                Das Ingenieurbüro Neubaur verlässt sich im Umgang mit seinen Kunden auf Werte, die funktionieren: 
                Zuverlässig und zielorientiert liefern wir ehrliche 
                Arbeit mit hoher Qualität zum vereinbarten Zeitpunkt.
              </p>
              <p className="text-center mb-4">
                Dabei betreut Sie bei allen Belangen
                Gerhard Neubaur, der Chef und 
                kompetente Ansprechpartner.
              </p>
              <p className="text-center mb-4">
                IB Neubaur schafft wirtschaftlich sinnvolle 
                Lösungen. Wir setzen Ihre Wünsche 
                mit schlüssigem Denken und jahrelanger 
                Erfahrung um.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Section */}
      <section className="section services">
        <div className="container">
          <div className="section-title">
            <h2>Unsere Fachgebiete</h2>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <Link key={index} to={service.link} style={{ textDecoration: 'none' }}>
                <motion.div 
                  className="service-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="service-content">
                    <h3 className="service-title">
                      <FontAwesomeIcon icon={service.icon} /> {service.title}
                    </h3>
                    <p className="service-text">{service.description}</p>
                    <div className="card-link">
                      Mehr erfahren <FontAwesomeIcon icon={faArrowRight} />
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="section features" style={{ backgroundColor: 'var(--gray-100)' }}>
        <div className="container">
          <div className="section-title">
            <h2>Unsere Leistungen für Sie</h2>
          </div>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              <h3 className="feature-title">LPH HOAI 1-9</h3>
              <p className="feature-text">
                Wir begleiten Sie durch alle Leistungsphasen der HOAI, von der Grundlagenermittlung bis zur Objektbetreuung.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              <h3 className="feature-title">Neubau bis Sanierung im Denkmalschutz</h3>
              <p className="feature-text">
                Ob Neubau oder sensible Sanierung im Denkmalschutz - wir haben die Expertise für Ihr Projekt.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              <h3 className="feature-title">Bestandsaufnahmen</h3>
              <p className="feature-text">
                Gründliche Bestandsaufnahmen als Basis für erfolgreiche Planungen und Sanierungen.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              <h3 className="feature-title">Von der Skizze bis zur Abnahme</h3>
              <p className="feature-text">
                Wir begleiten Sie durch den gesamten Prozess - von der ersten Skizze bis zur finalen Abnahme.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Projects Section */}
      <section className="section">
        <div className="container">
          <div className="section-title">
            <h2>Ausgewählte Referenzen</h2>
          </div>
          <div className="services-grid">
            {featuredProjects.map((project, index) => (
              <Link key={index} to={project.link} style={{ textDecoration: 'none' }}>
                <motion.div 
                  className="service-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  style={{ cursor: 'pointer' }}
                >
                  <img src={project.image} alt={project.title} className="card-img" />
                  <div className="service-content">
                    <h3 className="service-title">{project.title}</h3>
                    <p className="service-text">{project.description}</p>
                    <div className="card-link">
                      Mehr erfahren <FontAwesomeIcon icon={faArrowRight} />
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/referenzen" className="btn btn-primary">
              Alle Referenzen ansehen
            </Link>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Bereit für Ihr nächstes Projekt?</h2>
            <p className="cta-text">
              IB Neubaur – Wir steigern mit Sicherheit, Komfort und Energieeffizienz den Wert Ihres Objektes.
            </p>
            <div className="cta-buttons">
              <Link to="/kontakt" className="btn btn-lg">
                Kontakt aufnehmen
              </Link>
              <Link to="/referenzen" className="btn btn-outline">
                Referenzen ansehen
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Plan3D Section */}
      <section className="section">
        <div className="container">
          {/* Abschnittstitel entfernt */}
          <div className="row">
            <div className="col">
              <p className="text-center mb-4">
                Sie brauchen mehr Personal zum Zeichnen, um Spitzenlasten abzufangen? Nutzen Sie die Menpower von IB Neubaur. 
                Wir verstärken Ihr Team und konstruieren Ihre Pläne. Fristgerecht, kompetent, korrekt.
              </p>
              <p className="text-center mb-4">
                Unsere Mitarbeiter sind immer auf den neuesten Stand der Software geschult. Wir beherrschen unser Handwerk. 
                <strong> Es gibt immer eine Möglichkeit, es besser zu machen. Wir finden sie.</strong>
              </p>
              <p className="text-center mb-4">
                Aus z.B. dem Lastenheft/dem geplanten Nutzerprofil erstellen wir ein effizientes Pflichtenheft/Raumbuch. 
                Wir verstehen die Ziele und zeichnen/konstruieren/planen funktionierende Anwendungen. 
                Auf jeden Fall unterstützen wir Ihr Planungsvorhaben mit unserem Know-how und vertieften Wissen.
              </p>
              {/* Claim entfernt */}
              <div className="text-center">
                <Link to="/bim" className="btn btn-primary">
                  Mehr zu BIM und 3D-Planung
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;